import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { CASH_MAX_AMOUNT } from './money.js';

/**
 * Kaptan kasası (ADR 0093 §4, §5): para birimi başına ayrı kasa, her giriş ve çıkış ayrı ve
 * değiştirilemez işlem; bakiye işlemlerden türer, saklanmaz. Düzeltme ters kayıtla; kapanan ay
 * değişmez. Saf modül.
 */

export const CASH_TRANSACTION_TYPES = [
  'opening',
  'receipt',
  'ctm_received',
  'expense',
  'exchange_out',
  'exchange_in',
  'reversal',
  'adjustment',
] as const;
export type CashTxnType = (typeof CASH_TRANSACTION_TYPES)[number];
export type CashDirection = 'in' | 'out';

/** Elle girilen işlem türleri; ötekiler akıştan doğar (CTM teyidi, bozdurma, ters kayıt, sayım). */
export const CASH_MANUAL_TYPES = ['opening', 'receipt', 'expense'] as const;
export type CashManualType = (typeof CASH_MANUAL_TYPES)[number];

/**
 * Gider kategorileri: CTM harcama gerekçeleriyle aynı liste (kasa.pdf §2), ortağın sırasıyla.
 */
export const CASH_EXPENSE_CATEGORIES = [
  'crew_cash_advance',
  'crew_wages',
  'port_expenses',
  'provisions',
  'bonded_stores',
  'medical_expenses',
  'transportation',
  'repairs',
  'emergency_expenses',
  'other',
] as const;
export type CashExpenseCategory = (typeof CASH_EXPENSE_CATEGORIES)[number];
/** Giriş kategorileri; `ctm` yalnız CTM teyidinde, `exchange` bozdurmada, `cash_difference` sayımda. */
export const CASH_INCOME_CATEGORIES = ['other_income', 'refund', 'crew_repayment'] as const;
export const CASH_SYSTEM_CATEGORIES = ['ctm', 'exchange', 'cash_difference'] as const;
export const CASH_CATEGORIES = [
  ...CASH_EXPENSE_CATEGORIES,
  ...CASH_INCOME_CATEGORIES,
  ...CASH_SYSTEM_CATEGORIES,
] as const;
export type CashCategory = (typeof CASH_CATEGORIES)[number];

/** Payroll'dan bağlanan, elle girilemeyen kategoriler (ADR 0093 §6). */
export const PAYROLL_LINKED_CATEGORIES: readonly CashExpenseCategory[] = [
  'crew_cash_advance',
  'crew_wages',
];

/** Bağ türleri (ADR 0093 §4): gerçek bağ yalnız CTM ve Payroll'da; ötekiler serbest referans. */
export const CASH_LINK_TYPES = [
  'ctm_request',
  'payroll_entry',
  'payroll_payment',
  'purchase',
  'provisions',
  'port_expenses',
  'defect',
  'voyage',
  'document',
  'other',
] as const;
export type CashLinkType = (typeof CASH_LINK_TYPES)[number];
/** Sistemin kurduğu bağlar: elle seçilemez. */
export const CASH_SYSTEM_LINK_TYPES: readonly CashLinkType[] = [
  'ctm_request',
  'payroll_entry',
  'payroll_payment',
];
export const CASH_MANUAL_LINK_TYPES = CASH_LINK_TYPES.filter(
  (type) => !CASH_SYSTEM_LINK_TYPES.includes(type),
);

/** Ters kayıt ve sayım farkı gerekçesi sınırları. */
export const CASH_REVERSAL_REASON = { min: 5, max: 500 } as const;
export const CASH_DIFFERENCE_REASON = { min: 5, max: 1000 } as const;

const PREFIX: Readonly<Record<CashTxnType, string>> = {
  opening: 'OPN',
  receipt: 'RCV',
  ctm_received: 'CTM',
  expense: 'EXP',
  exchange_out: 'FXO',
  exchange_in: 'FXI',
  reversal: 'REV',
  adjustment: 'ADJ',
};

/** İşlem numarasının öneki (ekrandaki "EXP-087", "OPN-001" dili). */
export function txnPrefix(type: CashTxnType): string {
  return PREFIX[type];
}

export const TXN_NO = /^(OPN|RCV|CTM|EXP|FXO|FXI|REV|ADJ)-\d{4,}$/;

/** Gemi başına önek içinde sıradaki numara (`EXP-0088`). */
export function nextTxnNo(prefix: string, existing: readonly string[]): string {
  const max = existing.reduce((acc, no) => {
    const match = new RegExp(`^${prefix}-(\\d+)$`).exec(no);
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 0);
  return `${prefix}-${String(max + 1).padStart(4, '0')}`;
}

/**
 * İşlemin yönü türden gelir. Ters kaydın yönü tersine çevrilen işlemin yönünün tersidir; sayım
 * düzeltmesinin yönü farkın işaretinden (`given`).
 */
export function directionOf(type: CashTxnType, given?: CashDirection): CashDirection {
  switch (type) {
    case 'opening':
    case 'receipt':
    case 'ctm_received':
    case 'exchange_in':
      return 'in';
    case 'expense':
    case 'exchange_out':
      return 'out';
    case 'reversal':
      if (!given) throw new DomainError('INVALID_CASH_TRANSACTION', 'reversal');
      return given === 'in' ? 'out' : 'in';
    case 'adjustment':
      if (!given) throw new DomainError('INVALID_CASH_TRANSACTION', 'adjustment');
      return given;
  }
}

/** Defterin okuduğu en az alanlar. */
export interface CashTxn {
  id: string;
  txnNo: string;
  txnType: CashTxnType;
  direction: CashDirection;
  /** Hareketin anı, ISO-8601 UTC. */
  occurredAt: string;
  /** Gemi yerel günü. */
  txnDay: string;
  amount: number;
  currency: string;
  reversesId: string | null;
}

const signed = (t: Pick<CashTxn, 'direction' | 'amount'>) =>
  t.direction === 'in' ? t.amount : -t.amount;

/** Defter sırası: gün, an, numara (aynı anda girilen bağlı işlemler numarayla). */
export function compareTxns(a: CashTxn, b: CashTxn): number {
  return (
    a.txnDay.localeCompare(b.txnDay) ||
    a.occurredAt.localeCompare(b.occurredAt) ||
    a.txnNo.localeCompare(b.txnNo)
  );
}

export interface CashBalance {
  currency: string;
  balance: number;
}

/** Para birimine göre bakiye (kasalar bağımsız; kur yok). */
export function cashBalances(txns: readonly CashTxn[]): CashBalance[] {
  const map = new Map<string, number>();
  for (const t of txns) map.set(t.currency, (map.get(t.currency) ?? 0) + signed(t));
  return [...map]
    .map(([currency, balance]) => ({ currency, balance }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

export function balanceOf(txns: readonly CashTxn[], currency: string): number {
  return cashBalances(txns).find((b) => b.currency === currency)?.balance ?? 0;
}

export type LedgerRow<T extends CashTxn = CashTxn> = T & { balance: number };

/** Defter satırları, kendi kasasının yürüyen bakiyesiyle, defter sırasında. */
export function ledgerRows<T extends CashTxn>(txns: readonly T[]): LedgerRow<T>[] {
  const running = new Map<string, number>();
  return [...txns].sort(compareTxns).map((t) => {
    const balance = (running.get(t.currency) ?? 0) + signed(t);
    running.set(t.currency, balance);
    return { ...t, balance };
  });
}

export function cashMonthOf(day: string): string {
  return day.slice(0, 7);
}

export interface CashMonthSummary {
  currency: string;
  month: string;
  /** Önceki ayların toplamı ve aydaki açılış kaydı (ekranda "Opening Balance" satırı). */
  opening: number;
  received: number;
  paid: number;
  closing: number;
}

/** Ayın özeti (kasa.pdf §4): Opening, Total Cash Received, Total Cash Paid, Calculated Closing. */
export function monthSummary(
  txns: readonly CashTxn[],
  currency: string,
  month: string,
  /** Verilirse yalnız bu güne kadar (devir teslim sayımı). */
  untilDay?: string,
): CashMonthSummary {
  let opening = 0;
  let received = 0;
  let paid = 0;
  for (const t of txns) {
    if (t.currency !== currency) continue;
    const m = cashMonthOf(t.txnDay);
    if (m > month || (untilDay !== undefined && t.txnDay > untilDay)) continue;
    if (m < month || t.txnType === 'opening') opening += signed(t);
    else if (t.direction === 'in') received += t.amount;
    else paid += t.amount;
  }
  return { currency, month, opening, received, paid, closing: opening + received - paid };
}

/** Kasada olmayan nakit ödenemez (ADR 0093 §4). */
export function assertCashAvailable(balance: number, outAmount: number): void {
  if (outAmount > balance) {
    throw new DomainError('INSUFFICIENT_CASH', `${balance} < ${outAmount}`);
  }
}

/** Bir işlem bir kez ters çevrilir; ters kayıt ters çevrilmez. */
export function assertReversible(txn: Pick<CashTxn, 'txnType'>, alreadyReversed: boolean): void {
  if (txn.txnType === 'reversal') throw new DomainError('CANNOT_REVERSE_REVERSAL');
  if (alreadyReversed) throw new DomainError('ALREADY_REVERSED');
}

/** Kapanmış aya işlem girilmez (ADR 0093 §5). */
export function assertMonthOpen(closedMonths: ReadonlySet<string>, day: string): void {
  if (closedMonths.has(cashMonthOf(day))) throw new DomainError('MONTH_CLOSED', cashMonthOf(day));
}

function lastDayOf(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  const day = new Date(Date.UTC(year ?? 1970, mon ?? 1, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, '0')}`;
}

function nextMonth(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 1970, mon ?? 1, 1));
  return date.toISOString().slice(0, 7);
}

/**
 * Ay kapanabilir mi (ADR 0093 §5): ay bitmiş ya da bugün ayın son günü; kapanmamış; para
 * biriminin ilk işleminin ayından beri önceki aylar kapalı (sırayla).
 */
export function assertMonthClosable(input: {
  month: string;
  today: string;
  closed: ReadonlySet<string>;
  /** Para biriminin ilk işleminin ayı; işlem yoksa `null`. */
  firstMonth: string | null;
}): void {
  const { month, today, closed, firstMonth } = input;
  if (firstMonth === null || firstMonth > month) throw new DomainError('NO_CASH_ACCOUNT', month);
  if (closed.has(month)) throw new DomainError('MONTH_CLOSED', month);
  if (today < lastDayOf(month)) throw new DomainError('MONTH_NOT_ENDED', month);
  for (let m = firstMonth; m < month; m = nextMonth(m)) {
    if (!closed.has(m)) throw new DomainError('PREVIOUS_MONTH_OPEN', m);
  }
}

/**
 * Sayım (ADR 0093 §5): fark = sayılan − hesaplanan. Fark varsa gerekçe zorunlu ve defteri sayılan
 * nakde eşitleyen düzeltmenin yönü ve tutarı döner.
 */
export function reconcileCount(
  calculated: number,
  counted: number,
  reason: string | null,
): { difference: number; adjustment: { direction: CashDirection; amount: number } | null } {
  const difference = counted - calculated;
  if (difference === 0) return { difference, adjustment: null };
  const text = reason?.trim() ?? '';
  if (text.length < CASH_DIFFERENCE_REASON.min) {
    throw new DomainError('DIFFERENCE_REASON_REQUIRED');
  }
  return {
    difference,
    adjustment: { direction: difference > 0 ? 'in' : 'out', amount: Math.abs(difference) },
  };
}

/**
 * Gemi günü ve saati (`HH:MM`) ile gemi saati farkından UTC an. Saat verilmezse 12:00 (gün
 * içinde tarafsız; gün kayması olmaz).
 */
export function occurredAtOf(day: string, time: string | null | undefined, offsetMin: number) {
  const local = Date.parse(`${day}T${time ?? '12:00'}:00.000Z`);
  return new Date(local - offsetMin * 60_000).toISOString();
}

/* ---------- Şemalar (gemi, bulut ve arayüz ortak) ---------- */

const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const missing = (input: unknown) => input === undefined || input === null;
const typed = (c: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : c),
});
export const cashAmountSchema = (min = 1) =>
  z
    .number(typed('INVALID_AMOUNT'))
    .int('INVALID_AMOUNT')
    .min(min, 'INVALID_AMOUNT')
    .max(CASH_MAX_AMOUNT, 'TOO_LARGE');
export const cashText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .nullish()
    .transform((value) => (value ? value : null));
export const cashCurrencySchema = () =>
  z
    .string(typed('INVALID_CURRENCY'))
    .transform((value) => value.trim().toUpperCase())
    .pipe(z.string().regex(/^[A-Z]{3}$/, 'INVALID_CURRENCY'));
export const cashDaySchema = () =>
  z
    .string(typed('INVALID_DATE'))
    .regex(DAY, 'INVALID_DATE')
    .refine((value) => {
      const date = new Date(`${value}T00:00:00Z`);
      return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
    }, 'INVALID_DATE');
const time = () => z.string(typed('INVALID_TIME')).regex(TIME, 'INVALID_TIME').nullish();

function issuesOf(error: z.ZodError): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.message,
  }));
}

export function cashParser<T extends z.ZodType>(schema: T) {
  return (input: unknown): ParseResult<z.output<T>> => {
    const result = schema.safeParse(input);
    return result.success
      ? { ok: true, value: result.data }
      : { ok: false, issues: issuesOf(result.error) };
  };
}

const entrySchema = z
  .object({
    txnType: z.enum(CASH_MANUAL_TYPES, typed('INVALID_OPTION')),
    day: cashDaySchema(),
    time: time(),
    amount: cashAmountSchema(1),
    currency: cashCurrencySchema(),
    category: z.string(typed('INVALID_OPTION')).nullish(),
    payee: cashText(200),
    port: cashText(200),
    receiptNo: cashText(100),
    remarks: cashText(1000),
    linkedType: z.enum(CASH_LINK_TYPES, typed('INVALID_OPTION')).nullish(),
    linkedRef: cashText(100),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (value.txnType === 'expense') {
      if (!value.category) issue('category', 'REQUIRED');
      else if (!(CASH_EXPENSE_CATEGORIES as readonly string[]).includes(value.category)) {
        issue('category', 'INVALID_OPTION');
      } else if (PAYROLL_LINKED_CATEGORIES.includes(value.category as CashExpenseCategory)) {
        // Aynı gider ikinci kez elle girilemez: Payroll'dan bağlanır (ADR 0093 §6)
        issue('category', 'PAYROLL_LINK_REQUIRED');
      }
    }
    if (value.txnType === 'receipt') {
      if (!value.category) issue('category', 'REQUIRED');
      else if (!(CASH_INCOME_CATEGORIES as readonly string[]).includes(value.category)) {
        issue('category', 'INVALID_OPTION');
      }
    }
    if (value.linkedType && CASH_SYSTEM_LINK_TYPES.includes(value.linkedType)) {
      issue('linkedType', 'INVALID_OPTION');
    }
    if (value.linkedRef && !value.linkedType) issue('linkedType', 'REQUIRED');
  })
  .transform((value) => ({
    ...value,
    time: value.time ?? null,
    category:
      value.txnType === 'opening' ? null : ((value.category ?? null) as CashCategory | null),
    linkedType: value.linkedType ?? null,
  }));
export type CashEntryInput = z.output<typeof entrySchema>;
/** Elle girilen işlem: açılış, giriş ya da gider (ADR 0093 §4). */
export const parseCashEntry = cashParser(entrySchema);

const exchangeSchema = z
  .object({
    day: cashDaySchema(),
    time: time(),
    fromCurrency: cashCurrencySchema(),
    fromAmount: cashAmountSchema(1),
    toCurrency: cashCurrencySchema(),
    toAmount: cashAmountSchema(1),
    payee: cashText(200),
    port: cashText(200),
    receiptNo: cashText(100),
    remarks: cashText(1000),
  })
  .superRefine((value, ctx) => {
    if (value.fromCurrency === value.toCurrency) {
      ctx.addIssue({ code: 'custom', path: ['toCurrency'], message: 'SAME_CURRENCY' });
    }
  })
  .transform((value) => ({ ...value, time: value.time ?? null }));
export type CashExchangeInput = z.output<typeof exchangeSchema>;
/** Döviz bozdurma: iki kasa arasında bağlı çıkış ve giriş (ADR 0093 §2). */
export const parseCashExchange = cashParser(exchangeSchema);
